import React, { Fragment, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─────────────────────────────────────────────────────────────────
// API config
// ─────────────────────────────────────────────────────────────────
const API_BASE = WEBSERVICE;
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const api = {
  getAllGateways: (merchantId) =>
    fetch(`${API_BASE}/merchant/getAllPaymentGateway`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ merchantId: String(merchantId) }),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  addGateway: (payload) =>
    fetch(`${API_BASE}/merchant/addPaymentGateway`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  updateGateway: (payload) =>
    fetch(`${API_BASE}/merchant/updatePaymentGateway`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  deleteGateway: (merchantId, id, extra = {}) =>
    fetch(`${API_BASE}/merchant/deletePaymentGateway`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ merchantId: String(merchantId), id: String(id), ...extra }),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),
};

// ─────────────────────────────────────────────────────────────────
// Style tokens
// ─────────────────────────────────────────────────────────────────
const ResponsiveStyles = () => (
  <style>{`
    .ps-wrapper { padding: 36px 24px 60px; }
    .ps-header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
    .ps-card-header { display: flex; align-items: center; gap: 12px; padding: 20px 24px; border-bottom: 1px solid var(--body-color-less2); }
    .ps-card-body { padding: 24px; }
    .ps-grid { display: grid; gap: 18px; }
    .ps-grid-2 { grid-template-columns: 1fr 1fr; }
    .ps-grid-3 { grid-template-columns: 1fr 1fr 1fr; }
    .ps-footer-actions { display: flex; justify-content: flex-end; gap: 12px; align-items: center; margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--body-color-less2); }
    .ps-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }

    @media (max-width: 1024px) {
      .ps-grid-3 { grid-template-columns: 1fr 1fr; }
    }

    @media (max-width: 768px) {
      .ps-wrapper { padding: 20px 16px 40px; }
      .ps-header-row { flex-direction: column; gap: 12px; }
      .ps-card-header { flex-direction: column; align-items: flex-start; padding: 16px; }
      .ps-card-body { padding: 16px; }
      .ps-grid-2, .ps-grid-3 { grid-template-columns: 1fr; gap: 16px; }
      .ps-footer-actions, .ps-modal-actions { flex-direction: column-reverse; align-items: stretch; }
      .ps-footer-actions > button, .ps-footer-actions > div, .ps-modal-actions > button { width: 100% !important; justify-content: center !important; }
      .ps-card-header-sub { margin-top: 8px !important; }
    }
  `}</style>
);

const S = {
  pageWrapper: {}, // Moved to .ps-wrapper
  headerRow: {}, // Moved to .ps-header-row
  pageTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
  card: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    border: "1px solid var(--body-color-less2)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px",
    overflow: "hidden"
  },
  cardHeader: {}, // Moved to .ps-card-header
  cardHeaderIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0,
  },
  cardHeaderTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  cardHeaderSub: {
    fontSize: "12px",
    color: "var(--text-color)",
    opacity: 0.5,
    marginTop: "2px",
  },
  cardBody: {}, // Moved to .ps-card-body
  formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: "var(--text-color)",
    opacity: 0.7,
  },
  input: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  inputError: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid #ef4444",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  btnPrimary: {
    padding: "10px 24px",
    background: "var(--btn-color)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnOutline: {
    padding: "9px 18px",
    background: "transparent",
    border: "1px solid var(--btn-color)",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnDanger: {
    padding: "7px 14px",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.2)",
    color: "#ef4444",
    borderRadius: "9px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  },
  btnGhost: {
    padding: "7px 14px",
    background: "transparent",
    border: "1px solid var(--body-color-less2)",
    color: "var(--text-color)",
    borderRadius: "9px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    opacity: 0.7,
  },
  errorText: {
    fontSize: "11px",
    color: "#ef4444",
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  badge: {
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.3px",
  },
  helperText: {
    fontSize: "11px",
    color: "var(--text-color)",
    opacity: 0.45,
    marginTop: "2px",
  },
};

// ─────────────────────────────────────────────────────────────────
// Shared UI helpers
// ─────────────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: "36px",
        height: "20px",
        borderRadius: "10px",
        background: value ? "var(--btn-color)" : "var(--body-color-less2)",
        position: "relative",
        transition: "background 0.2s",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: "3px",
          left: value ? "19px" : "3px",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </div>
  );
}

function SaveButton({ onClick, saving, saved }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...S.btnPrimary,
        minWidth: "120px",
        justifyContent: "center",
        opacity: saving ? 0.7 : 1,
      }}
      disabled={saving}
    >
      {saving ? (
        <>
          <i className="mdi mdi-loading mdi-spin" /> Saving…
        </>
      ) : saved ? (
        <>
          <i className="mdi mdi-check-circle" /> Saved
        </>
      ) : (
        <>
          <i className="mdi mdi-content-save-outline" /> Save Changes
        </>
      )}
    </button>
  );
}

function ConfirmationModal({ isOpen, title, message, onConfirm, onCancel, confirmText = "Clear Keys", cancelText = "Cancel", isDanger = false }) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "var(--body-color-less)",
          borderRadius: "16px",
          border: "1px solid var(--body-color-less2)",
          padding: "28px 24px",
          maxWidth: "420px",
          width: "90%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: isDanger ? "rgba(239,68,68,0.1)" : "rgba(59,130,246,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className={`mdi ${isDanger ? "mdi-alert-circle-outline" : "mdi-help-circle-outline"}`}
              style={{
                fontSize: "20px",
                color: isDanger ? "#ef4444" : "#3b82f6",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "var(--text-color)",
                margin: 0,
                marginBottom: "4px",
              }}
            >
              {title}
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "var(--text-color)",
                opacity: 0.65,
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              {message}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "24px",
          }}
        >
          <button
            onClick={onCancel}
            style={{
              ...S.btnGhost,
              minWidth: "100px",
              justifyContent: "center",
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              ...S.btnDanger,
              minWidth: "100px",
              justifyContent: "center",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, error }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        style={{
          ...(error ? S.inputError : S.input),
          paddingRight: "44px",
          fontFamily: show ? "inherit" : "'SF Mono','Fira Code',monospace",
          fontSize: "13px",
        }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        style={{
          position: "absolute",
          right: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-color)",
          opacity: 0.4,
          padding: "2px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <i
          className={`mdi ${show ? "mdi-eye-off-outline" : "mdi-eye-outline"}`}
          style={{ fontSize: "16px" }}
        />
      </button>
    </div>
  );
}

function MonoInput({ value, onChange, placeholder, icon, error }) {
  return (
    <div style={{ position: "relative" }}>
      {icon && (
        <i
          className={`mdi ${icon}`}
          style={{
            position: "absolute",
            left: "13px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "15px",
            opacity: 0.35,
            color: "var(--text-color)",
            pointerEvents: "none",
          }}
        />
      )}
      <input
        type="text"
        style={{
          ...(error ? S.inputError : S.input),
          paddingLeft: icon ? "38px" : "14px",
          fontFamily: "'SF Mono','Fira Code',monospace",
          fontSize: "13px",
        }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 1. Stripe Settings Card
// ─────────────────────────────────────────────────────────────────
function StripeCard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Populate from loaded gateway data
  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        clientSecret: gateway.clientSecret || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Publishable key is required";
      else if (!form.clientId.startsWith("pk_"))
        e.clientId = "Publishable key must start with pk_live_ or pk_test_";
      if (!form.clientSecret.trim()) e.clientSecret = "Secret key is required";
      else if (!form.clientSecret.startsWith("sk_"))
        e.clientSecret = "Secret key must start with sk_live_ or sk_test_";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        // Update existing
        const res = await api.updateGateway({
          merchantId: String(merchantId),
          id: String(gatewayId),
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          isActive: form.enabled ? "1" : "0",
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to update PayBito Apex settings."
          );
          return;
        }
      } else {
        // Create new
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "Stripe",
          clientId: form.clientId,
          clientSecret: form.clientSecret,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to save PayBito Apex settings."
          );
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Apex settings saved successfully.");
      // Refetch gateways to ensure UI updates with new gateway ID
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const stripeGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName === "Stripe"
          );
          if (stripeGw) {
            setGatewayId(stripeGw.id);
            setForm({
              enabled: stripeGw.isActive === 1,
              clientId: stripeGw.clientId || "",
              clientSecret: stripeGw.clientSecret || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId);
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to clear PayBito Apex keys."
          );
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Apex keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #635bff 12%, transparent)",
              color: "#635bff",
            }}
          >
            <i className="mdi mdi-credit-card" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Apex</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #635bff 12%, transparent)",
                  color: "#635bff",
                }}
              >
                Card Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Apex — enter your API credentials below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #635bff 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #635bff 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "16px",
                color: "#635bff",
                flexShrink: 0,
                marginTop: "1px",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.7,
                lineHeight: "1.6",
              }}
            >
              Retrieve your API keys from the{" "}
              <strong style={{ opacity: 1 }}>
                Stripe Dashboard → Developers → API Keys
              </strong>
              . Use{" "}
              <code
                style={{
                  background: "rgba(99,91,255,0.1)",
                  padding: "1px 5px",
                  borderRadius: "4px",
                  fontSize: "11px",
                }}
              >
                pk_live_
              </code>{" "}
              /{" "}
              <code
                style={{
                  background: "rgba(99,91,255,0.1)",
                  padding: "1px 5px",
                  borderRadius: "4px",
                  fontSize: "11px",
                }}
              >
                sk_live_
              </code>{" "}
              for production.
            </div>
          </div>

          <div className="ps-grid ps-grid-2">
            <div style={S.formGroup}>
              <label style={S.label}>
                Publishable Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="pk_live_xxxxxxxxxxxx"
                icon="mdi-key-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i
                    className="mdi mdi-alert-circle-outline"
                    style={{ fontSize: "12px" }}
                  />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your public-facing PayBito Apex key (safe to expose client-side)
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="sk_live_xxxxxxxxxxxx"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i
                    className="mdi mdi-alert-circle-outline"
                    style={{ fontSize: "12px" }}
                  />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  Keep this secret — never expose in frontend code
                </span>
              )}
            </div>
          </div>

          <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{
                  ...S.btnDanger,
                  opacity: saving ? 0.7 : 1,
                }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. PayPal Settings Card
// ─────────────────────────────────────────────────────────────────
function PayPalCard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        clientSecret: gateway.clientSecret || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Client ID is required";
      if (!form.clientSecret.trim()) e.clientSecret = "Secret key is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          merchantId: String(merchantId),
          id: String(gatewayId),
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          isActive: form.enabled ? "1" : "0",
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to update PayBito Titan settings."
          );
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "Paypal",
          clientId: form.clientId,
          clientSecret: form.clientSecret,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to save PayBito Titan settings."
          );
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Titan settings saved successfully.");
      // Refetch gateways to ensure UI updates with new gateway ID
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const paypalGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName === "Paypal"
          );
          if (paypalGw) {
            setGatewayId(paypalGw.id);
            setForm({
              enabled: paypalGw.isActive === 1,
              clientId: paypalGw.clientId || "",
              clientSecret: paypalGw.clientSecret || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId);
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to clear PayBito Titan keys."
          );
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Titan keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #003087 12%, transparent)",
              color: "#009cde",
            }}
          >
            <i className="mdi mdi-paypal" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Titan</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #009cde 12%, transparent)",
                  color: "#009cde",
                }}
              >
                PayBito Titan Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept PayBito Titan payments — enter your PayBito Titan REST API credentials
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #009cde 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #009cde 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "16px",
                color: "#009cde",
                flexShrink: 0,
                marginTop: "1px",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.7,
                lineHeight: "1.6",
              }}
            >
              Retrieve your credentials from{" "}
              <strong style={{ opacity: 1 }}>
                PayPal Developer Dashboard → My Apps & Credentials
              </strong>
              . Create a REST API app to get your Client ID and Secret.
            </div>
          </div>

          <div className="ps-grid ps-grid-2">
            <div style={S.formGroup}>
              <label style={S.label}>
                Client ID <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="AXxxxxxxxxxxxxxxxxxxxx"
                icon="mdi-identifier"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i
                    className="mdi mdi-alert-circle-outline"
                    style={{ fontSize: "12px" }}
                  />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>Your PayBito Titan REST API Client ID</span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="EKxxxxxxxxxxxxxxxxxxxx"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i
                    className="mdi mdi-alert-circle-outline"
                    style={{ fontSize: "12px" }}
                  />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Titan REST API Secret — keep this confidential
                </span>
              )}
            </div>
          </div>

          <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{
                  ...S.btnDanger,
                  opacity: saving ? 0.7 : 1,
                }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. KurvPay Settings Card
// ─────────────────────────────────────────────────────────────────
function KurvPayCard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
    secretKeyV4: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        clientSecret: gateway.clientSecret || "",
        secretKeyV4: gateway.secretKeyV4 || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Collect.js Tokenization Key is required";
      if (!form.clientSecret.trim()) e.clientSecret = "Secret Key is required";
      if (!form.secretKeyV4.trim()) e.secretKeyV4 = "V4 Secret Key is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          merchantId: String(merchantId),
          id: String(gatewayId),
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
          isActive: form.enabled ? "1" : "0",
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to update PayBito Nexus settings.");
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "KurvPay",
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to save PayBito Nexus settings.");
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Nexus settings saved successfully.");
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const kurvpayGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName?.toLowerCase() === "kurvpay"
          );
          if (kurvpayGw) {
            setGatewayId(kurvpayGw.id);
            setForm({
              enabled: kurvpayGw.isActive === 1,
              clientId: kurvpayGw.clientId || "",
              clientSecret: kurvpayGw.clientSecret || "",
              secretKeyV4: kurvpayGw.secretKeyV4 || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId, { secretKeyV4: form.secretKeyV4 });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to clear PayBito Nexus keys.");
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "", secretKeyV4: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Nexus keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the PayBito Nexus API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #0d9488 12%, transparent)",
              color: "#0d9488",
            }}
          >
            <i className="mdi mdi-flash" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Nexus</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #0d9488 12%, transparent)",
                  color: "#0d9488",
                }}
              >
                Card Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Nexus — enter your Collect.js tokenization key and secret below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          {/* Info box */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #0d9488 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #0d9488 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "16px",
                color: "#0d9488",
                flexShrink: 0,
                marginTop: "1px",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.7,
                lineHeight: "1.7",
              }}
            >
              <strong style={{ opacity: 1 }}>How to set up your PayBito Nexus credentials:</strong>
              <ol style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                <li>Log in to your <strong style={{ opacity: 1 }}>PayBito Nexus Merchant Portal</strong>.</li>
                <li>Navigate to <strong style={{ opacity: 1 }}>Settings → Security Keys</strong>.</li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Collect.js Tokenization Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Public Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Public Key</strong>.</li>
                    <li>Select <strong style={{ opacity: 1 }}>Key Permission</strong> as <strong style={{ opacity: 1 }}>Tokenization</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Tokenization Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>API</strong> permission by selecting the <strong style={{ opacity: 1 }}>API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a V4 Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>V4 API</strong> permission by selecting the <strong style={{ opacity: 1 }}>V4 API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>V4 Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>

          <div className="ps-grid ps-grid-3">
            <div style={S.formGroup}>
              <label style={S.label}>
                Collect.js Tokenization Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="your-tokenization-key"
                icon="mdi-key-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your Collect.js tokenization key from the PayBito Nexus portal
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="your-secret-key"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  Keep this secret — never expose in frontend code
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                V4 Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.secretKeyV4}
                onChange={(e) => set("secretKeyV4", e.target.value)}
                placeholder="your-v4-secret-key"
                error={errors.secretKeyV4}
              />
              {errors.secretKeyV4 ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.secretKeyV4}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Nexus V4 secret key for enhanced authentication
                </span>
              )}
            </div>
          </div>

          {/* <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{ ...S.btnDanger, opacity: saving ? 0.7 : 1 }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div> */}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. HMS Settings Card
// ─────────────────────────────────────────────────────────────────
function HMSCard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
    secretKeyV4: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        clientSecret: gateway.clientSecret || "",
        secretKeyV4: gateway.secretKeyV4 || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Tokenization Key is required";
      if (!form.clientSecret.trim()) e.clientSecret = "Secret Key is required";
      if (!form.secretKeyV4.trim()) e.secretKeyV4 = "V4 Secret Key is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          merchantId: String(merchantId),
          id: String(gatewayId),
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
          isActive: form.enabled ? "1" : "0",
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to update PayBito Nova settings.");
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "HostMerchantServices",
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to save PayBito Nova settings.");
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Nova settings saved successfully.");
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const hmsGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName === "HostMerchantServices"
          );
          if (hmsGw) {
            setGatewayId(hmsGw.id);
            setForm({
              enabled: hmsGw.isActive === 1,
              clientId: hmsGw.clientId || "",
              clientSecret: hmsGw.clientSecret || "",
              secretKeyV4: hmsGw.secretKeyV4 || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId, { secretKeyV4: form.secretKeyV4 });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to clear PayBito Nova keys.");
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "", secretKeyV4: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Nova keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the PayBito Nova API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #0d9488 12%, transparent)",
              color: "#0d9488",
            }}
          >
            <i className="mdi mdi-flash" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Nova</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #0d9488 12%, transparent)",
                  color: "#0d9488",
                }}
              >
                Card Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Nova — enter your Collect.js tokenization key and secret below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          {/* Info box */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #0d9488 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #0d9488 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{ fontSize: "16px", color: "#0d9488", flexShrink: 0, marginTop: "1px" }}
            />
            <div
              style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.7" }}
            >
              <strong style={{ opacity: 1 }}>How to set up your PayBito Nova credentials:</strong>
              <ol style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                <li>Log in to your <strong style={{ opacity: 1 }}>PayBito Nova Merchant Portal</strong>.</li>
                <li>Navigate to <strong style={{ opacity: 1 }}>Settings → Security Keys</strong>.</li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Collect.js Tokenization Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Public Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Public Key</strong>.</li>
                    <li>Select <strong style={{ opacity: 1 }}>Key Permission</strong> as <strong style={{ opacity: 1 }}>Tokenization</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Tokenization Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>API</strong> permission by selecting the <strong style={{ opacity: 1 }}>API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a V4 Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>V4 API</strong> permission by selecting the <strong style={{ opacity: 1 }}>V4 API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>V4 Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>

          <div className="ps-grid ps-grid-3">
            <div style={S.formGroup}>
              <label style={S.label}>
                Collect.js Tokenization Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="your-tokenization-key"
                icon="mdi-key-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your Collect.js tokenization key from the PayBito Nova portal
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="your-secret-key"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  Keep this secret — never expose in frontend code
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                V4 Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.secretKeyV4}
                onChange={(e) => set("secretKeyV4", e.target.value)}
                placeholder="your-v4-secret-key"
                error={errors.secretKeyV4}
              />
              {errors.secretKeyV4 ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.secretKeyV4}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Nova V4 secret key for enhanced authentication
                </span>
              )}
            </div>
          </div>

          {/* <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{ ...S.btnDanger, opacity: saving ? 0.7 : 1 }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div> */}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 5. NMI Settings Card
// ─────────────────────────────────────────────────────────────────
function NMICard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
    secretKeyV4: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        clientSecret: gateway.clientSecret || "",
        secretKeyV4: gateway.secretKeyV4 || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Collect.js Tokenization Key is required";
      if (!form.clientSecret.trim()) e.clientSecret = "Secret Key is required";
      if (!form.secretKeyV4.trim()) e.secretKeyV4 = "V4 Secret Key is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          merchantId: String(merchantId),
          id: String(gatewayId),
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
          isActive: form.enabled ? "1" : "0",
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to update PayBito Zenith settings.");
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "NMI",
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          secretKeyV4: form.secretKeyV4,
        });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to save PayBito Zenith settings.");
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Zenith settings saved successfully.");
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const nmiGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName === "NMI"
          );
          if (nmiGw) {
            setGatewayId(nmiGw.id);
            setForm({
              enabled: nmiGw.isActive === 1,
              clientId: nmiGw.clientId || "",
              clientSecret: nmiGw.clientSecret || "",
              secretKeyV4: nmiGw.secretKeyV4 || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId, { secretKeyV4: form.secretKeyV4 });
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to clear PayBito Zenith keys.");
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "", secretKeyV4: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Zenith keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the PayBito Zenith API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #0d9488 12%, transparent)",
              color: "#0d9488",
            }}
          >
            <i className="mdi mdi-flash" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Zenith</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #0d9488 12%, transparent)",
                  color: "#0d9488",
                }}
              >
                Card Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Zenith — enter your Collect.js tokenization key and secret below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          {/* Info box */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #0d9488 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #0d9488 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{ fontSize: "16px", color: "#0d9488", flexShrink: 0, marginTop: "1px" }}
            />
            <div
              style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.7" }}
            >
              <strong style={{ opacity: 1 }}>How to set up your PayBito Zenith credentials:</strong>
              <ol style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                <li>Log in to your <strong style={{ opacity: 1 }}>PayBito Zenith Merchant Portal</strong>.</li>
                <li>Navigate to <strong style={{ opacity: 1 }}>Settings → Security Keys</strong>.</li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Collect.js Tokenization Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Public Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Public Key</strong>.</li>
                    <li>Select <strong style={{ opacity: 1 }}>Key Permission</strong> as <strong style={{ opacity: 1 }}>Tokenization</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Tokenization Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>API</strong> permission by selecting the <strong style={{ opacity: 1 }}>API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
                <li>
                  <strong style={{ opacity: 1 }}>Generate a V4 Secret Key</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    <li>Navigate to <strong style={{ opacity: 1 }}>Private Security Keys</strong>.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Add New Private Key</strong>.</li>
                    <li>Enable the <strong style={{ opacity: 1 }}>V4 API</strong> permission by selecting the <strong style={{ opacity: 1 }}>V4 API</strong> checkbox.</li>
                    <li>Click <strong style={{ opacity: 1 }}>Create Key</strong>.</li>
                    <li>Copy the generated key and paste it into the <strong style={{ opacity: 1 }}>V4 Secret Key</strong> field on your PayBito Payment Setup page.</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>

          <div className="ps-grid ps-grid-3">
            <div style={S.formGroup}>
              <label style={S.label}>
                Collect.js Tokenization Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="your-tokenization-key"
                icon="mdi-key-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your Collect.js tokenization key from the PayBito Zenith portal
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="your-secret-key"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  Keep this secret — never expose in frontend code
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                V4 Secret Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.secretKeyV4}
                onChange={(e) => set("secretKeyV4", e.target.value)}
                placeholder="your-v4-secret-key"
                error={errors.secretKeyV4}
              />
              {errors.secretKeyV4 ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.secretKeyV4}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Zenith V4 secret key for enhanced authentication
                </span>
              )}
            </div>
          </div>

          {/* <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{ ...S.btnDanger, opacity: saving ? 0.7 : 1 }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div> */}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 6. NetBilling Settings Card
// ─────────────────────────────────────────────────────────────────
function NetBillingCard({ gateway, merchantId }) {
  const [form, setForm] = useState({
    enabled: false,
    clientId: "", // Used for Account ID
    clientSecret: "", // Used for Site Tag
    authorization: "",
    controlKeyword: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.accountId || gateway.clientId || "",
        clientSecret: gateway.siteTag || gateway.clientSecret || "",
        authorization: gateway.authorization || "",
        controlKeyword: gateway.controlKeyword || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    // Account ID should be numeric only
    if (key === "clientId") {
      val = val.replace(/\D/g, "");
    }
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (form.enabled) {
      if (!form.clientId.trim()) e.clientId = "Account ID is required";
      if (!form.clientSecret.trim()) e.clientSecret = "Site Tag is required";
      if (!form.authorization.trim()) e.authorization = "Authorization is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          id: String(gatewayId),
          merchantId: String(merchantId),
          gatewayName: "netbilling",
          accountId: form.clientId,
          siteTag: form.clientSecret,
          authorization: form.authorization,
          controlKeyword: form.controlKeyword,
          isActive: form.enabled ? 1 : 0,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to update PayBito Vertex settings."
          );
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "netbilling",
          clientId: "",
          clientSecret: "",
          accountId: form.clientId,
          siteTag: form.clientSecret,
          authorization: form.authorization,
          controlKeyword: form.controlKeyword,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to save PayBito Vertex settings."
          );
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Vertex settings saved successfully.");
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const nbGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName?.toLowerCase() === "netbilling"
          );
          if (nbGw) {
            setGatewayId(nbGw.id);
            setForm({
              enabled: nbGw.isActive === 1,
              clientId: nbGw.accountId || nbGw.clientId || "",
              clientSecret: nbGw.siteTag || nbGw.clientSecret || "",
              authorization: nbGw.authorization || "",
              controlKeyword: nbGw.controlKeyword || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId);
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to clear PayBito Vertex keys.");
          return;
        }
        setForm((f) => ({ ...f, clientId: "", clientSecret: "", authorization: "", controlKeyword: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Vertex keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the PayBito Vertex API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #004a99 12%, transparent)",
              color: "#004a99",
            }}
          >

            <i className="mdi mdi-credit-card" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Vertex</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #004a99 12%, transparent)",
                  color: "#004a99",
                }}
              >
                Card Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Vertex — enter your Account ID and Site Tag below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #004a99 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #004a99 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "16px",
                color: "#004a99",
                flexShrink: 0,
                marginTop: "1px",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.7,
                lineHeight: "1.6",
              }}
            >
              Retrieve your Account ID and Site Tag from the{" "}
              <strong style={{ opacity: 1 }}>PayBito Vertex Merchant Portal</strong>.
            </div>
          </div>

          <div className="ps-grid ps-grid-2">
            <div style={S.formGroup}>
              <label style={S.label}>
                Account ID <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="123456"
                icon="mdi-account-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your numeric PayBito Vertex Account ID
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Site Tag <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                placeholder="SITE_TAG"
                icon="mdi-tag-outline"
                error={errors.clientSecret}
              />
              {errors.clientSecret ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientSecret}
                </span>
              ) : (
                <span style={S.helperText}>
                  The Site Tag assigned to your account
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Authorization <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.authorization}
                onChange={(e) => set("authorization", e.target.value)}
                placeholder="Authorization Key"
                icon="mdi-shield-check-outline"
                error={errors.authorization}
              />
              {errors.authorization ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.authorization}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Vertex authorization key
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Control keyword
              </label>
              <MonoInput
                value={form.controlKeyword}
                onChange={(e) => set("controlKeyword", e.target.value)}
                placeholder="Control keyword"
                icon="mdi-lock-outline"
              />
              <span style={S.helperText}>
                The control keyword for your PayBito Vertex account
              </span>
            </div>
          </div>

          <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{ ...S.btnDanger, opacity: saving ? 0.7 : 1 }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 6. CardFlo Settings Card
// ─────────────────────────────────────────────────────────────────
function CardFloCard({ gateway, merchantId }) {
  const { isTestMode } = useSelector((state) => state);
  const [form, setForm] = useState({
    enabled: true,
    clientId: "",
    userName: "",
    password: "",
    cashierKey: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gatewayId, setGatewayId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (gateway) {
      setGatewayId(gateway.id);
      setForm({
        enabled: gateway.isActive === 1,
        clientId: gateway.clientId || "",
        userName: gateway.userName || "",
        password: gateway.password || "",
        cashierKey: gateway.cashierKey || "",
      });
    }
  }, [gateway]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setSaved(false);
  };

  const validate = () => {
    const e = {};
    if (!form.clientId.trim()) e.clientId = "API Key is required";
    if (!form.userName.trim()) e.userName = "Username is required";
    if (!form.password.trim()) e.password = "Password is required";
    if (!form.cashierKey.trim()) e.cashierKey = "Cashier Key is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (!validate()) return;
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.updateGateway({
          id: String(gatewayId),
          merchantId: String(merchantId),
          gatewayName: "cardflo",
          clientId: form.clientId,
          userName: form.userName,
          password: form.password,
          cashierKey: form.cashierKey,
          isActive: form.enabled ? 1 : 0,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to update PayBito Sovereign settings."
          );
          return;
        }
      } else {
        const res = await api.addGateway({
          merchantId: String(merchantId),
          gatewayName: "cardflo",
          clientId: form.clientId,
          userName: form.userName,
          password: form.password,
          cashierKey: form.cashierKey,
        });
        if (res.error !== 0) {
          showToast(
            "error",
            res.error_msg || "Failed to save PayBito Sovereign settings."
          );
          return;
        }
        if (res.returnId) setGatewayId(res.returnId);
      }
      setSaved(true);
      showToast("success", "PayBito Sovereign settings saved successfully.");
      try {
        const gatewaysRes = await api.getAllGateways(merchantId);
        if (gatewaysRes.error === 0) {
          const cfGw = (gatewaysRes.payment_gateways || []).find(
            (g) => g.gatewayName === "cardflo"
          );
          if (cfGw) {
            setGatewayId(cfGw.id);
            setForm({
              enabled: cfGw.isActive === 1,
              clientId: cfGw.clientId || "",
              userName: cfGw.userName || "",
              password: cfGw.password || "",
              cashierKey: cfGw.cashierKey || "",
            });
          }
        }
      } catch (err) {
        console.error("Failed to refetch gateways", err);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearKeys = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      if (gatewayId) {
        const res = await api.deleteGateway(merchantId, gatewayId);
        if (res.error !== 0) {
          showToast("error", res.error_msg || "Failed to clear PayBito Sovereign keys.");
          return;
        }
        setForm((f) => ({ ...f, clientId: "", userName: "", password: "", cashierKey: "" }));
        setGatewayId(null);
        setSaved(true);
        showToast("success", "PayBito Sovereign keys cleared successfully.");
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Clear API Keys"
        message="Are you sure you want to clear the PayBito Sovereign API keys? This action cannot be undone."
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirm(false)}
        confirmText="Clear Keys"
        isDanger={true}
      />
      <div style={S.card}>
        <div className="ps-card-header" style={S.cardHeader}>
          <div
            style={{
              ...S.cardHeaderIcon,
              background: "color-mix(in srgb, #4f46e5 12%, transparent)",
              color: "#4f46e5",
            }}
          >
            <i className="mdi mdi-credit-card" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={S.cardHeaderTitle}>PayBito Sovereign</h3>
              <span
                style={{
                  ...S.badge,
                  background: "color-mix(in srgb, #4f46e5 12%, transparent)",
                  color: "#4f46e5",
                }}
              >
                PayBito Sovereign Payments
              </span>
            </div>
            <div style={S.cardHeaderSub}>
              Accept card payments via PayBito Sovereign — enter your credentials below
            </div>
          </div>
        </div>

        <div className="ps-card-body" style={S.cardBody}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "color-mix(in srgb, #4f46e5 6%, var(--body-color))",
              border: "1px solid color-mix(in srgb, #4f46e5 18%, transparent)",
              marginBottom: "22px",
            }}
          >
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "16px",
                color: "#4f46e5",
                flexShrink: 0,
                marginTop: "1px",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.7,
                lineHeight: "1.7",
              }}
            >
              <strong style={{ opacity: 1 }}>How to configure PayBito Sovereign Checkout:</strong>
              <ol style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                <li>Log in to your <strong style={{ opacity: 1 }}>PayBito Sovereign Dashboard</strong>.</li>
                <li>Navigate to the <strong style={{ opacity: 1 }}>Checkouts</strong> link.</li>
                <li>Add a checkout with the following URLs:</li>
              </ol>
              <div style={{ marginTop: "12px", background: "var(--bg-secondary, rgba(0,0,0,0.02))", padding: "10px", borderRadius: "6px", border: "1px solid var(--border-color, rgba(0,0,0,0.05))" }}>
                <div style={{ marginBottom: "8px" }}>
                  <strong style={{ opacity: 1 }}>Success Redirect URL:</strong> <br />
                  <code style={{ fontSize: "11px", wordBreak: "break-all", userSelect: "all", cursor: "copy", background: "rgba(0,0,0,0.04)", padding: "2px 4px", borderRadius: "3px" }}>{window.location.origin}/merchant/checkout/success</code>
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <strong style={{ opacity: 1 }}>Cancel Redirect URL:</strong> <br />
                  <code style={{ fontSize: "11px", wordBreak: "break-all", userSelect: "all", cursor: "copy", background: "rgba(0,0,0,0.04)", padding: "2px 4px", borderRadius: "3px" }}>{window.location.origin}/merchant/checkout/cancel</code>
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <strong style={{ opacity: 1 }}>Failure Redirect URL:</strong> <br />
                  <code style={{ fontSize: "11px", wordBreak: "break-all", userSelect: "all", cursor: "copy", background: "rgba(0,0,0,0.04)", padding: "2px 4px", borderRadius: "3px" }}>{window.location.origin}/merchant/checkout/failure</code>
                </div>
                <div>
                  <strong style={{ opacity: 1 }}>Webhook Notification URL:</strong> <br />
                  <code style={{ fontSize: "11px", wordBreak: "break-all", userSelect: "all", cursor: "copy", background: "rgba(0,0,0,0.04)", padding: "2px 4px", borderRadius: "3px" }}>
                    {isTestMode
                      ? `https://accounts.paybito.com/SandboxWebhook/cardflowebhook/${merchantId}`
                      : `https://accounts.paybito.com/WebhookProject/webhook/cardflowebhook/${merchantId}`
                    }
                  </code>
                </div>
              </div>
              <ol start="4" style={{ margin: "12px 0 0", paddingLeft: "18px" }}>
                <li>Activate the checkout in the PayBito Sovereign dashboard.</li>
                <li>Enter your PayBito Sovereign API credentials below. Keep this information secure.</li>
              </ol>
            </div>
          </div>

          <div className="ps-grid ps-grid-2">
            <div style={S.formGroup}>
              <label style={S.label}>
                API Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                placeholder="PAYBITO_SOVEREIGN_API_KEY"
                icon="mdi-key-outline"
                error={errors.clientId}
              />
              {errors.clientId ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.clientId}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your unique PayBito Sovereign API key
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Username <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <MonoInput
                value={form.userName}
                onChange={(e) => set("userName", e.target.value)}
                placeholder="paybito_sovereign_user"
                icon="mdi-account-outline"
                error={errors.userName}
              />
              {errors.userName ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.userName}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Sovereign account username
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Password <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="paybito_sovereign_pass"
                error={errors.password}
              />
              {errors.password ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.password}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Sovereign account password
                </span>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>
                Cashier Key <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <PasswordInput
                value={form.cashierKey}
                onChange={(e) => set("cashierKey", e.target.value)}
                placeholder="PAYBITO_SOVEREIGN_CASHIER_KEY"
                error={errors.cashierKey}
              />
              {errors.cashierKey ? (
                <span style={S.errorText}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                  {errors.cashierKey}
                </span>
              ) : (
                <span style={S.helperText}>
                  Your PayBito Sovereign cashier authentication key
                </span>
              )}
            </div>
          </div>

          <div className="ps-footer-actions">
            {gatewayId && (
              <button
                onClick={handleClearKeys}
                style={{ ...S.btnDanger, opacity: saving ? 0.7 : 1 }}
                disabled={saving}
              >
                <i className="mdi mdi-delete-outline" /> Clear Keys
              </button>
            )}
            <SaveButton onClick={handleSave} saving={saving} saved={saved} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// ROOT component
// ─────────────────────────────────────────────────────────────────
export default function PaymentSettings() {
  const { isTestMode } = useSelector((state) => state);
  const merchantId = getMerchantId();
  const [stripeGateway, setStripeGateway] = useState(null);
  const [paypalGateway, setPaypalGateway] = useState(null);
  const [kurvpayGateway, setKurvpayGateway] = useState(null);
  const [netbillingGateway, setNetbillingGateway] = useState(null);
  const [hmsGateway, setHmsGateway] = useState(null);
  const [nmiGateway, setNmiGateway] = useState(null);
  const [cardfloGateway, setCardfloGateway] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .getAllGateways(merchantId)
      .then((res) => {
        if (res.error !== 0) return;
        const gateways = res.payment_gateways || [];
        setStripeGateway(
          gateways.find((g) => g.gatewayName === "Stripe") || null
        );
        setPaypalGateway(
          gateways.find((g) => g.gatewayName === "Paypal") || null
        );
        setKurvpayGateway(
          gateways.find((g) => g.gatewayName?.toLowerCase() === "kurvpay") || null
        );
        setNetbillingGateway(
          gateways.find((g) => g.gatewayName?.toLowerCase() === "netbilling") || null
        );
        setHmsGateway(
          gateways.find((g) => g.gatewayName === "HostMerchantServices") || null
        );
        setNmiGateway(
          gateways.find((g) => g.gatewayName === "NMI") || null
        );
        setCardfloGateway(
          gateways.find((g) => g.gatewayName === "cardflo") || null
        );
      })
      .catch((err) => showHttpError(err))
      .finally(() => setLoading(false));
  }, [merchantId]);

  return (
    <Fragment>
      <ResponsiveStyles />
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="ps-wrapper" style={S.pageWrapper}>
            <div className="ps-header-row" style={S.headerRow}>
              <div>
                <h2 style={S.pageTitle}>Payment Setup</h2>
                <div style={S.pageSubtitle}>
                  Manage payment gateway credentials and API keys
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <i
                  className="mdi mdi-loading mdi-spin"
                  style={{
                    fontSize: "28px",
                    color: "var(--btn-color)",
                    display: "block",
                    marginBottom: "12px",
                  }}
                />
                <div
                  style={{
                    fontSize: "13px",
                    color: "var(--text-color)",
                    opacity: 0.5,
                  }}
                >
                  Loading Payment Setup…
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "var(--text-color)",
                      opacity: 0.55,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Gateway Credentials
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: "1px",
                      background: "var(--body-color-less2)",
                    }}
                  />
                </div>

                <StripeCard gateway={stripeGateway} merchantId={merchantId} />
                <PayPalCard gateway={paypalGateway} merchantId={merchantId} />
                <KurvPayCard gateway={kurvpayGateway} merchantId={merchantId} />
                <HMSCard gateway={hmsGateway} merchantId={merchantId} />
                <NMICard gateway={nmiGateway} merchantId={merchantId} />
                <NetBillingCard
                  gateway={netbillingGateway}
                  merchantId={merchantId}
                />
                <CardFloCard
                  gateway={cardfloGateway}
                  merchantId={merchantId}
                />
              </>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
